import { useState, useCallback, useEffect, useRef } from 'react';
import { Chess } from 'chess.js';
import { detectOpening, CommentEngine, MoveComment, buildEvidencePackage, EvidencePackage, levelToDepth, levelToElo } from '@chess-platform/shared';
import { engineManager, EngineEvaluation } from '../game/engine/engineManager';
import { audioManager } from '../../utils/audioManager';

const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export interface MoveMetadata {
    fen: string;
    uci?: string;
    san?: string;
    evaluation?: EngineEvaluation;
    comment?: MoveComment;
    /** Structured evidence package for the coach panel */
    evidence?: EvidencePackage;
}

export function useAnalysisLogic() {
    const [history, setHistory] = useState<MoveMetadata[]>([{ fen: STARTING_FEN }]);
    const [historyIndex, setHistoryIndex] = useState(0);
    const [isEngineOn, setIsEngineOn] = useState(true);
    const [isEngineReady, setIsEngineReady] = useState(false);

    // Analysis Level (controls engine depth)
    const [analysisElo, setAnalysisElo] = useState<number>(20); // Default to max (2800 ELO)

    // Feature toggles
    const [showComments, setShowComments] = useState(false);
    const [showTraps, setShowTraps] = useState(false);
    const [isDeepAnalysis, setIsDeepAnalysis] = useState(false);
    const [deepEval, setDeepEval] = useState<EngineEvaluation | null>(null);
    const [deepLoading, setDeepLoading] = useState(false);

    // Live evaluation state
    const [evaluation, setEvaluation] = useState<EngineEvaluation>({
        score: null, bestMove: null, isMate: false, mateMoves: null, multipv: []
    });

    const chessRef = useRef(new Chess());
    const currentFen = history[historyIndex].fen;
    const uciHistory = history.map(h => h.uci).filter(Boolean) as string[];

    // Engine lifecycle and subscription
    useEffect(() => {
        engineManager.init();
        if (engineManager.getIsReady()) {
            setIsEngineReady(true);
        } else {
            engineManager.onReady(() => setIsEngineReady(true));
        }

        engineManager.setMultiPv(3);

        const unsub = engineManager.subscribe((evalu) => {
            setEvaluation(evalu);
            if (evalu.score === null) return;

            setHistory(prev => {
                const current = prev[historyIndex];
                if (!current) return prev;

                // CRITICAL: Require minimum depth for classification to avoid "garbage in, garbage out"
                if (evalu.depth !== undefined && evalu.depth < 10) return prev;

                const existingDepth = current.evidence?.engine.depth || 0;
                if (evalu.depth && evalu.depth < existingDepth && current.evaluation) return prev;

                const nextHistory = [...prev];
                const updatedItem = { ...current, evaluation: evalu };

                // We need the previous move's evaluation to compute the delta
                if (historyIndex > 0) {
                    const prevMeta = prev[historyIndex - 1];
                    const prevEval = prevMeta.evaluation || { score: 0, bestMove: '', isMate: false };
                    const evalBefore = prevEval.score || 0;
                    const evalAfter = evalu.score || 0;

                    // Build structured EvidencePackage
                    if (current.uci && current.san) {
                        const engineLines = (evalu.multipv || []).map((line, i) => ({
                            multipv: i + 1,
                            scoreCp: Math.round((line.score ?? 0) * 100),
                            pvUci: line.move ? [line.move] : [],
                            depth: evalu.depth || 15,
                        }));

                        try {
                            updatedItem.evidence = buildEvidencePackage({
                                ply: historyIndex,
                                fenBefore: prevMeta.fen,
                                fenAfter: current.fen,
                                moveUci: current.uci,
                                moveSan: current.san,
                                evalBeforePawns: evalBefore,
                                evalAfterPawns: evalAfter,
                                isMate: evalu.isMate,
                                mateMoves: evalu.mateMoves || null,
                                mateBefore: prevEval.isMate ? (prevEval.mateMoves || null) : null,
                                bestMoveUci: prevEval.bestMove || '',
                                engineLines,
                                engineDepth: evalu.depth || 15,
                                uciHistory: uciHistory.slice(0, historyIndex),
                            });
                        } catch (e) {
                            console.warn('Evidence build failed:', e);
                        }
                    }
                }

                nextHistory[historyIndex] = updatedItem;
                return nextHistory;
            });
        });

        return () => {
            unsub();
            engineManager.stop();
        };
    }, [historyIndex, uciHistory.length]);

    useEffect(() => {
        try {
            if (chessRef.current.fen() !== currentFen) {
                chessRef.current.load(currentFen);
            }
        } catch {
            chessRef.current = new Chess(currentFen);
        }

        // --- INSTANT EVALUATION FROM HISTORY ---
        const meta = history[historyIndex];
        if (meta.evaluation) {
            setEvaluation(meta.evaluation);
        } else {
            // Only clear evaluation if we don't have it and engine is OFF
            if (!isEngineOn) {
                setEvaluation({ score: null, bestMove: null, isMate: false, mateMoves: null, multipv: [] });
            }
        }

        if (isEngineOn && isEngineReady) {
            // Instant detection for game over states
            const chessIns = new Chess(currentFen);
            if (chessIns.isGameOver()) {
                const isCheckmate = chessIns.isCheckmate();
                const sideToMove = chessIns.turn();
                const score = isCheckmate ? (sideToMove === 'w' ? -999 : 999) : 0;
                setEvaluation({
                    score,
                    isMate: isCheckmate,
                    mateMoves: 0,
                    bestMove: null,
                    multipv: [],
                    depth: 20
                });
            } else {
                // If we have evidence with deep enough analysis, maybe skip?
                // For now, let's allow it to keep analyzing to get deeper values, but the Eval Bar is already updated.
                engineManager.analyze(currentFen, `depth ${levelToDepth(analysisElo)}`, historyIndex, analysisElo);
            }
        } else if (!isEngineOn) {
            engineManager.stop();
        }
    }, [currentFen, isEngineOn, isEngineReady, analysisElo, historyIndex]);

    const makeMove = useCallback((from: string, to: string) => {
        try {
            const tempChess = new Chess(currentFen);
            const result = tempChess.move({ from, to, promotion: 'q' });
            if (result) {
                // Play sounds
                if (result.captured) {
                    audioManager.play('capture');
                } else if (tempChess.inCheck()) {
                    audioManager.play('check');
                } else if (result.flags.includes('k') || result.flags.includes('q')) {
                    audioManager.play('castle');
                } else if (result.flags.includes('p')) {
                    audioManager.play('promote');
                } else {
                    audioManager.play('move');
                }

                if (tempChess.isCheckmate()) {
                    audioManager.play('game-end');
                }

                const newMetadata: MoveMetadata = {
                    fen: tempChess.fen(),
                    uci: result.from + result.to + (result.promotion || ''),
                    san: result.san
                };

                const newHistory = [...history.slice(0, historyIndex + 1), newMetadata];
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
                return true;
            }
        } catch { }
        return false;
    }, [currentFen, history, historyIndex]);

    const goBack = () => {
        if (historyIndex > 0) setHistoryIndex(historyIndex - 1);
    };

    const goForward = () => {
        if (historyIndex < history.length - 1) setHistoryIndex(historyIndex + 1);
    };

    const resetBoard = () => {
        setHistory([{ fen: STARTING_FEN }]);
        setHistoryIndex(0);
        if (isEngineOn) engineManager.analyze(STARTING_FEN, 'depth 15', 0, analysisElo);
    };

    const loadFen = (fen: string) => {
        try {
            const testChess = new Chess(fen.trim());
            const newFen = testChess.fen();
            setHistory([{ fen: newFen }]);
            setHistoryIndex(0);
            return true;
        } catch {
            return false;
        }
    };

    const toggleEngine = () => setIsEngineOn(!isEngineOn);

    const runDeepAnalysis = async (square: string) => {
        if (!isEngineReady) return;
        setDeepLoading(true);
        setIsDeepAnalysis(true);
        const res = await engineManager.getMovesForSquare(currentFen, square);
        setDeepEval(res);
        setDeepLoading(false);
    };

    const toggleDeepAnalysis = () => {
        const next = !isDeepAnalysis;
        setIsDeepAnalysis(next);
        if (!next) {
            setDeepEval(null);
        }
    };

    const currentOpening = detectOpening(uciHistory.slice(0, historyIndex + 1));
    const openingName = currentOpening?.name || (uciHistory.length > 5 && historyIndex > 5 ? 'Posición de juego' : null);
    const currentEvidence = history[historyIndex]?.evidence ?? null;

    const evidenceArrows: [string, string, string][] = [];
    if (currentEvidence) {
        for (const arrow of currentEvidence.ui.suggestedArrows) {
            evidenceArrows.push([arrow.from, arrow.to, arrow.color]);
        }
    }

    const evidenceHighlights: Record<string, React.CSSProperties> = {};
    if (currentEvidence) {
        for (const sq of currentEvidence.ui.suggestedHighlights) {
            evidenceHighlights[sq] = {
                background: 'rgba(251,191,36,0.35)',
                boxShadow: 'inset 0 0 0 2px rgba(251,191,36,0.7)',
            };
        }
    }

    return {
        currentFen,
        history,
        historyIndex,
        isEngineOn,
        isEngineReady,
        evaluation,
        currentOpening,
        openingName,
        showComments,
        setShowComments,
        showTraps,
        setShowTraps,
        analysisElo,
        setAnalysisElo,
        makeMove,
        goBack,
        goForward,
        resetBoard,
        loadFen,
        toggleEngine,
        setHistoryIndex,
        isDeepAnalysis,
        toggleDeepAnalysis,
        runDeepAnalysis,
        deepEval,
        deepLoading,
        currentEvidence,
        evidenceArrows,
        evidenceHighlights,
    };
}

import type React from 'react';
